import React from 'react';
import { GlobeIcon, SmartphoneIcon, BarChart3Icon, IdCardIcon } from 'lucide-react';
import { architectureNodes, atnaNode, heroIdentity, type ArchitectureNodeId } from '../../data/heroContent';
const NODE_ICONS: Record<ArchitectureNodeId, typeof GlobeIcon> = {
  website: GlobeIcon,
  mobile: SmartphoneIcon,
  dashboard: BarChart3Icon
};
const LINE = 'bg-[#9B9EEA]';
const DIAGRAM_FR = 'https://cdn.magicpatterns.com/uploads/aqyDWi72QHvCuVymBvze5A/ChatGPT_Image_19_sept._2026,_12_47_14.png';
const DIAGRAM_EN = 'https://cdn.magicpatterns.com/uploads/fURbB4dCVEDDAzP3JsA95R/ChatGPT_Image_19_sept._2026,_12_55_04.png';
function IdentityCard({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="border border-[#E4E6EC] bg-white shadow-[0_1px_2px_rgba(18,18,18,0.04)] px-5 py-4" data-id="element-3709" data-mp="3043642aoq0lw">
      <div className="flex items-start gap-3" data-id="element-3710" data-mp="19rf1eu8v7scw">
        <span className="w-9 h-9 shrink-0 bg-[#4F46E5]/[0.07] flex items-center justify-center" data-id="element-3711" data-mp="19sgmzsnibx8v">
          <IdCardIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} data-id="element-3712" data-mp="15sep7ukw7nyv" />
        </span>
        <div className="min-w-0" data-id="element-3713" data-mp="1unyatxj8z50k">
          <div className="font-sans text-sm font-semibold text-[#121212]" data-id="element-3714" data-mp="1zl7yp8qzn8ma">
            {isFR ? heroIdentity.title.fr : heroIdentity.title.en}
          </div>
          <div className="font-sans text-xs text-[#5B6478] mt-0.5" data-id="element-3715" data-mp="24iqar80ay3yi">
            {isFR ? heroIdentity.caption.fr : heroIdentity.caption.en}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[#E4E6EC] flex items-center gap-3" data-id="element-3716" data-mp="2e92sksgqfj0y">
        {heroIdentity.chips.map((chip, index) => <React.Fragment key={chip.en} data-id="element-3717" data-mp="2idsky7c6mfh4" data-mpi={index}>
            {index > 0 && <span className="w-px h-3 bg-[#E4E6EC]" aria-hidden="true" data-id="element-3718" data-mp="2nc3tmeqqqjxr" />}
            <span className="font-sans text-[11px] text-[#5B6478]" data-id="element-3719" data-mp="2s8uc18q0prmc">
              {isFR ? chip.fr : chip.en}
            </span>
          </React.Fragment>)}
      </div>
    </div>;
}
export function HeroArchitecture({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="w-full" data-id="element-3720" data-mp="puwryt2v9xxy">
      {/* DESKTOP / TABLET — full architecture visual */}
      <img src={isFR ? DIAGRAM_FR : DIAGRAM_EN} alt={isFR ? 'Architecture BCITI | ATNA : identité et carte numérique reliées au site web, à l’application mobile, à ATNA — intelligence agentique — et au tableau de bord, connectés aux systèmes, données et services.' : 'BCITI | ATNA architecture: digital identity and card connected to the website, mobile app, ATNA — agentic intelligence — and the dashboard, linked to systems, data and services.'} className="hidden md:block w-full h-auto object-contain" data-id="element-3721" data-mp="ltl1klc658op" />

      {/* MOBILE — same story, simplified rather than scaled down */}
      <div className="md:hidden" data-id="element-3722" data-mp="1dm27t88t4q89">
        <IdentityCard isFR={isFR} data-id="element-3723" data-mp="1dn3l7jdapot5" />

        <div className="relative h-8 flex justify-center items-center" aria-hidden="true" data-id="element-3724" data-mp="1sh8fs0rgqz48">
          <span className={`w-px h-full ${LINE}`} data-id="element-3725" data-mp="1nk88jmivnqbj" />
        </div>

        <div className="border border-[#4F46E5]/20 bg-white px-5 py-4 text-center" data-id="element-3726" data-mp="27505s9f4lcq0">
          <div className="font-serif text-xl tracking-[0.18em] text-[#121212]" data-id="element-3727" data-mp="2b6bu8bm9jrwm">
            ATNA
          </div>
          <div className="font-sans text-xs font-medium text-[#4F46E5] mt-1" data-id="element-3728" data-mp="1x6sxeeywk2zm">
            {isFR ? atnaNode.title.fr : atnaNode.title.en}
          </div>
          <div className="font-sans text-[11px] text-[#5B6478] mt-2" data-id="element-3729" data-mp="223sw24tfa3gp">
            {atnaNode.verbs.map(verb => isFR ? verb.fr : verb.en).join(' · ')}
          </div>
        </div>

        <div className="relative h-8 flex justify-center items-center" aria-hidden="true" data-id="element-3730" data-mp="1s89d0izyiz9d">
          <span className={`w-px h-full ${LINE}`} data-id="element-3731" data-mp="1ehw2c8vol6gc" />
        </div>

        <div className="grid grid-cols-3 gap-2" data-id="element-3732" data-mp="1jg7ewrdd1v29">
          {architectureNodes.map(node => {
          const Icon = NODE_ICONS[node.id];
          return <div key={node.id} className="border border-[#E4E6EC] bg-white px-2 py-3 text-center" data-id="element-3733" data-mp="19goygkwnsrey">
                <Icon className="w-4 h-4 text-[#4F46E5] mx-auto mb-1.5" strokeWidth={1.75} data-id="element-3734" data-mp="2crcjcvdimk4h" />
                <div className="font-sans text-[11px] font-semibold text-[#121212] leading-tight" data-id="element-3735" data-mp="2322nd4fjwhb3">
                  {isFR ? node.title.fr : node.title.en}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}